import { useState } from 'react'
import { Plus, Pencil, Trash2, ChevronDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useConfigGeneral, useCreateConfigGeneral, useUpdateConfigGeneral, useDeleteConfigGeneral,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { ConfigGeneralFormDialog } from './ConfigGeneralFormDialog'
import type { ConfigGeneral, ConfigGeneralEntity } from '@/modules/multichannel/types/configuracion'
import { CONFIG_GENERAL_LABELS } from '@/modules/multichannel/types/configuracion'

const ENTITIES: ConfigGeneralEntity[] = [
  'tipos-campania', 'formatos-campania', 'estados-contenido',
  'estados-envio', 'estados-reporte', 'estados-aprobacion', 'tipos-envio',
]

function EntitySection({ entity }: { entity: ConfigGeneralEntity }) {
  const [isOpen, setIsOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ConfigGeneral | null>(null)

  const { data: items } = useConfigGeneral(entity)
  const createMutation = useCreateConfigGeneral(entity)
  const updateMutation = useUpdateConfigGeneral(entity)
  const deleteMutation = useDeleteConfigGeneral(entity)

  const label = CONFIG_GENERAL_LABELS[entity]

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  {label}
                  {items && <Badge className="badge-gradient text-xs">{items.length}</Badge>}
                </CardTitle>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-3">
              <div className="flex justify-end">
                <Button onClick={() => { setEditing(null); setDialogOpen(true) }} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
                  <Plus className="mr-2 h-4 w-4" /> Nuevo
                </Button>
              </div>
              {items && items.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Descripcion</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(item => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.nombre}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{item.descripcion || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch checked={item.activo}
                                onCheckedChange={() => updateMutation.mutate({ id: item.id, data: { activo: !item.activo } })}
                                className={item.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                              <Badge className={item.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                                {item.activo ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditing(item); setDialogOpen(true) }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={async () => {
                                if (window.confirm(`Eliminar "${item.nombre}"?`)) await deleteMutation.mutateAsync(item.id)
                              }}>
                                <Trash2 className="h-4 w-4 text-gray-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">No hay elementos creados.</div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <ConfigGeneralFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null) }}
        item={editing}
        entityLabel={label}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </>
  )
}

export function ConfigGeneralTab() {
  return (
    <div className="space-y-3">
      {ENTITIES.map(entity => (
        <EntitySection key={entity} entity={entity} />
      ))}
    </div>
  )
}
